import React, { useState, useRef, useEffect } from "react";
import invoke from "../../common/utils/invoke";
import {
  validateCareerEmail,
  validateMobileNumber,
  validateRequired,
  validateFileSize,
} from "../../common/utils/validation";
import "./CreateGrievanceRedressalForm.scss";
import BrandLoader from "../../common/components/BrandLoader.jsx";
import Modal from "../../common/components/Modal.jsx";
import { logError } from "../../common/utils/logger.js";
import IndiaFlag from "../../assets/images/india-flag.png";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { HTTP_STATUS, ERROR_MESSAGES } from "../../constants";

const IDENTIFY_BY = {
  POLICY_NUMBER: "policyNumber",
  CLIENT_ID: "clientId",
  MOBILE_AND_DOB: "mobileAndDob",
};

const GRIEVANCE_TYPES = [
  { value: "deathClaims", label: "Death Claims" },
  { value: "policyServicing", label: "Policy Servicing" },
  { value: "proposalProcessing", label: "Proposal Processing" },
  { value: "survivalClaims", label: "Survival Claims" },
  { value: "ulipRelated", label: "ULIP Related" },
  { value: "unfairBusinessPractices", label: "Unfair Business Practices" },
  { value: "other", label: "Other" },
];

/** Backend marks an already-open service request as informational instead of an error. */
const INFO_CODE_SERVICE_REQUEST_ALREADY_OPEN = "SERVICE_REQUEST_ALREADY_OPEN";
const SERVICE_REQUEST_ALREADY_OPEN_MESSAGE =
  "A service request of this type is already open.";

const FILE_ACCEPT = ".jpg,.jpeg,.png,.pdf";
const FILE_MAX_MB = 5;
const GRIEVANCE_DESC_MAX = 1500;
const OTP_LENGTH = 6;

const INITIAL_FIRST = {
  isIndusindCustomer: null,
  identifyBy: null,
  policyNumber: "",
  clientId: "",
  mobileNumber: "",
  dob: "",
};

const INITIAL_SECOND = {
  email: "",
  mobile: "",
  grievanceType: "",
  grievanceDescription: "",
  documents: null,
};

const INITIAL_SECOND_IDENTIFY = {
  identifyBy: IDENTIFY_BY.POLICY_NUMBER,
  policyNumber: "",
  clientId: "",
  mobileNumber: "",
  dob: "",
};

const DEFAULT_HEADER_TITLE = "Report your grievance";

/** Converts a File to base64 string for API (attachments must be base64). Strips whitespace so XML/CDATA is clean. */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      let base64 = typeof dataUrl === "string" ? dataUrl.split(",")[1] || "" : "";
      base64 = base64.replace(/\s/g, "");
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const CreateGrievanceRedressalForm = ({ pageTitle }) => {
  const otpRefs = useRef({});
  const otpSlots = useRef(
    Array.from({ length: OTP_LENGTH }, (_, i) => ({
      id: `otp-slot-${i + 1}`,
      pos: i,
    })),
  );

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMsg, setApiErrorMsg] = useState("");
  const [showAlreadyOpenModal, setShowAlreadyOpenModal] = useState(false);
  const [alreadyOpenMsg, setAlreadyOpenMsg] = useState("");
  const [isFirstForm, setIsFirstForm] = useState(true);
  const [firstFormData, setFirstFormData] = useState(INITIAL_FIRST);
  const [secondFormData, setSecondFormData] = useState(INITIAL_SECOND);
  const [policyInfo, setPolicyInfo] = useState(null);
  const [dobDate, setDobDate] = useState(null);
  const [dobDateSecond, setDobDateSecond] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedGrievanceNo, setSubmittedGrievanceNo] = useState("");
  const [secondFormIsIndusindCustomer, setSecondFormIsIndusindCustomer] =
    useState(true);
  const [secondFormIdentify, setSecondFormIdentify] = useState(
    INITIAL_SECOND_IDENTIFY,
  );
  const [autoFilledEmail, setAutoFilledEmail] = useState(false);
  const [autoFilledMobile, setAutoFilledMobile] = useState(false);

  // OTP States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpId, setOtpId] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [pendingPolicyInfo, setPendingPolicyInfo] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerificationToken, setOtpVerificationToken] = useState(null);

  // Reset form to initial state on mount to ensure first form is always shown
  useEffect(() => {
    setIsFirstForm(true);
    setFirstFormData(INITIAL_FIRST);
    setSecondFormData(INITIAL_SECOND);
    setSecondFormIdentify(INITIAL_SECOND_IDENTIFY);
    setPolicyInfo(null);
    setDobDate(null);
    setDobDateSecond(null);
    setShowSuccessModal(false);
    setSubmittedGrievanceNo("");
    setShowAlreadyOpenModal(false);
    setAlreadyOpenMsg("");
    setSecondFormIsIndusindCustomer(true);
    setAutoFilledEmail(false);
    setAutoFilledMobile(false);
    setErrors({});
    setShowOtpModal(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpId(null);
    setOtpError(null);
    setPendingPolicyInfo(null);
    setOtpVerified(false);
    setOtpVerificationToken(null);
  }, []);

  // OTP Handlers
  const handleOtpChange = (e) => {
    const value = e.target.value;
    const pos = Number(e.target.dataset.pos);

    if (!/^\d?$/.test(value)) return;

    setOtp((prev) => {
      const updated = [...prev];
      updated[pos] = value;
      return updated;
    });

    if (value) {
      const next = otpRefs.current[pos + 1];
      next?.focus();
    }
  };

  const handleOtpKeyDown = (e) => {
    const pos = Number(e.target.dataset.pos);

    if (e.key === "Backspace" && !otp[pos]) {
      otpRefs.current[pos - 1]?.focus();
    }

    if (e.key === "Enter") {
      const firstEmpty = otp.findIndex((d) => d === "");
      if (firstEmpty !== -1) {
        otpRefs.current[firstEmpty]?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, OTP_LENGTH);

    if (!digits) return;

    const updatedOtp = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < digits.length; i++) {
      updatedOtp[i] = digits[i];
    }
    setOtp(updatedOtp);

    const lastIndex = Math.min(digits.length - 1, OTP_LENGTH - 1);
    otpRefs.current[lastIndex]?.focus();
  };

  const handleFirstChange = (e) => {
    const { name, value, type } = e.target;
    const checked = type === "radio" ? e.target.value === "true" : undefined;

    // If "No" is selected for isIndusindCustomer, automatically switch to second form
    if (name === "isIndusindCustomer" && checked === false) {
      setFirstFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      setSecondFormIsIndusindCustomer(false);
      setIsFirstForm(false);
      setSecondFormData(INITIAL_SECOND);
      setSecondFormIdentify(INITIAL_SECOND_IDENTIFY);
      setDobDateSecond(null);
      setAutoFilledEmail(false);
      setAutoFilledMobile(false);
      setPolicyInfo(null);
      setErrors({});
      return;
    }

    setFirstFormData((prev) => ({
      ...prev,
      [name]: type === "radio" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleIdentifyByChange = (e) => {
    const value = e.target.value;
    setFirstFormData((prev) => ({
      ...prev,
      identifyBy: value,
      policyNumber: "",
      clientId: "",
      mobileNumber: "",
      dob: "",
    }));
    setDobDate(null);
    setErrors((prev) => ({
      ...prev,
      policyNumber: "",
      clientId: "",
      mobileNumber: "",
      dob: "",
    }));
  };

  const handleRadioOptionBlockClick = (value, handler) => (e) => {
    if (
      e.target.closest("input:not([type=radio])") ||
      e.target.closest(".react-datepicker")
    ) {
      return;
    }
    handler({ target: { value } });
  };

  const handleDobChange = (date) => {
    setDobDate(date);
    // Format as DD-MM-YYYY for backend
    const formatted = date
      ? `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`
      : "";
    setFirstFormData((prev) => ({ ...prev, dob: formatted }));
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: "" }));
  };

  const validatePolicyNumber = (policyNumber, errors) => {
    const v = (policyNumber || "").replace(/\s/g, "");
    if (!v) errors.policyNumber = "Policy number is required";
  };

  const validateClientId = (clientId, errors) => {
    if (!(clientId || "").trim()) errors.clientId = "Client ID is required";
  };

  const validateMobileAndDob = (mobileNumber, dob, errors) => {
    const mob = (mobileNumber || "").replace(/\D/g, "");
    if (mob.length !== 10)
      errors.mobileNumber = "Enter valid 10-digit mobile number";
    if (!(dob || "").trim()) errors.dob = "Date of birth is required";
  };

  const validateFirstForm = () => {
    const newErrors = {};
    const {
      isIndusindCustomer,
      identifyBy,
      policyNumber,
      clientId,
      mobileNumber,
      dob,
    } = firstFormData;

    if (isIndusindCustomer === null) {
      newErrors.isIndusindCustomer =
        "Please select whether you are an IndusInd Nippon Life Insurance customer";
    }
    if (!isIndusindCustomer) {
      setErrors(newErrors);
      return true;
    }

    if (identifyBy === IDENTIFY_BY.POLICY_NUMBER) {
      validatePolicyNumber(policyNumber, newErrors);
    } else if (identifyBy === IDENTIFY_BY.CLIENT_ID) {
      validateClientId(clientId, newErrors);
    } else if (identifyBy === IDENTIFY_BY.MOBILE_AND_DOB) {
      validateMobileAndDob(mobileNumber, dob, newErrors);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildApiParams = () => {
    const { identifyBy, policyNumber, clientId, mobileNumber, dob } =
      firstFormData;
    if (identifyBy === IDENTIFY_BY.POLICY_NUMBER) {
      return { policyNumber: (policyNumber || "").replace(/\s/g, "") };
    }
    if (identifyBy === IDENTIFY_BY.CLIENT_ID) {
      return { clientId: (clientId || "").trim() };
    }
    return {
      mobileNumber: (mobileNumber || "").replace(/\D/g, ""),
      dob: (dob || "").trim(),
    };
  };

  const handleNonCustomerFlow = () => {
    setIsFirstForm(false);
    setSecondFormData(INITIAL_SECOND);
    setSecondFormIdentify(INITIAL_SECOND_IDENTIFY);
    setDobDateSecond(null);
    setAutoFilledEmail(false);
    setAutoFilledMobile(false);
    setPolicyInfo(null);
  };

  const showErrorAndStopLoading = (errorMsg) => {
    setApiErrorMsg(errorMsg);
    setShowErrorModal(true);
    setLoading(false);
  };

  const extractPolicyData = (table) => {
    const email = (table.Email || "").trim();
    const mobile = (table.Mobile || table.Telephone1 || "")
      .trim()
      .replace(/\D/g, "")
      .slice(-10);
    return { email, mobile };
  };

  const handleOtpGeneration = async (mobile) => {
    setLoading(true);
    try {
      const otpResponse = await invoke.generateOTP(mobile);
      if (
        otpResponse?.status !== "success" &&
        otpResponse?.status !== HTTP_STATUS.OK
      ) {
        showErrorAndStopLoading(
          otpResponse?.error ||
          otpResponse?.message ||
          ERROR_MESSAGES.OTP_FAILED,
        );
        return false;
      }
      setOtpId(otpResponse?.otpId ?? otpResponse?.id ?? "");
      setOtp(Array(OTP_LENGTH).fill(""));
      setShowOtpModal(true);
      setLoading(false);
      return true;
    } catch (otpErr) {
      logError("[CreateGrievanceRedressal] generateOTP failed", otpErr);
      showErrorAndStopLoading(
        otpErr?.response?.data?.error ||
        otpErr?.response?.data?.message ||
        ERROR_MESSAGES.OTP_FAILED,
      );
      return false;
    }
  };

  const proceedToSecondForm = (table, email, mobile) => {
    setPolicyInfo(table);
    setAutoFilledEmail(!!email);
    setAutoFilledMobile(!!mobile);
    setSecondFormData((prev) => ({
      ...prev,
      email: email || prev.email,
      mobile: mobile || prev.mobile,
    }));
    setSecondFormIdentify(INITIAL_SECOND_IDENTIFY);
    setDobDateSecond(null);
    setIsFirstForm(false);
    setLoading(false);
  };

  const isCustomerSelected = firstFormData.isIndusindCustomer === true || firstFormData.isIndusindCustomer === false;

const radioField = firstFormData.isIndusindCustomer === false || !!firstFormData.identifyBy;
  const handleFirstContinue = async (e) => {
    e.preventDefault();
    setApiErrorMsg("");
    setOtpError(null);

    const { isIndusindCustomer } = firstFormData;

    if (!validateFirstForm()) return;

    setSecondFormIsIndusindCustomer(isIndusindCustomer);

    if (isIndusindCustomer === false) {
      handleNonCustomerFlow();
      return;
    }

    setLoading(true);
    try {
      const params = buildApiParams();
      const response = await invoke.getLatestPolicyInfo(params);

      if (!response) {
        showErrorAndStopLoading(
          "Unable to fetch policy details. Please try again later.",
        );
        return;
      }

      if (typeof response?.operationResponse === "string") {
        showErrorAndStopLoading(
          response.operationResponse ||
          "No records found for the provided details.",
        );
        return;
      }

      const table = response?.operationResponse?.Table;
      if (!table) {
        showErrorAndStopLoading("No records found for the provided details.");
        return;
      }

      const { email, mobile } = extractPolicyData(table);
      setPendingPolicyInfo({ table, email, mobile });

      if (mobile && mobile.length === 10) {
        const otpSuccess = await handleOtpGeneration(mobile);
        if (otpSuccess) return;
      }

      proceedToSecondForm(table, email, mobile);
    } catch (err) {
      logError("[CreateGrievanceRedressal] getLatestPolicyInfo failed", err);
      showErrorAndStopLoading(
        err?.response?.data?.error || "Something went wrong. Please try again.",
      );
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== OTP_LENGTH) {
      setOtpError("Please enter valid 6-digit OTP");
      return;
    }

    if (!pendingPolicyInfo) {
      setOtpError("Session expired. Please try again.");
      return;
    }

    setOtpLoading(true);
    setOtpError(null);

    try {
      // VAPT: Send verified snapshot so server locks policy/client/mobile/email and rejects modified values on submit
      const { table, email, mobile } = pendingPolicyInfo;
      const policyNo = (table?.PolicyNumber || table?.PolicyNo || "").trim();
      const clientNo = (table?.ClientId || table?.ClientNo || "").trim();
      const mobileNo = (mobile || "").replace(/\D/g, "").slice(-10);
      const emailId = (email || table?.Email || "").trim();
      const verifiedSnapshot = {
        policyNo,
        clientNo,
        mobileNo,
        emailId,
      };

      const verifyResponse = await invoke.verifyOTP({
        otpId,
        identifier: pendingPolicyInfo.mobile,
        otp: enteredOtp,
        verifiedSnapshot,
      });

      if (
        verifyResponse?.status !== "success" &&
        verifyResponse?.verified !== true
      ) {
        throw new Error("Invalid OTP");
      }

      // VAPT: Only proceed when server returned a verification token. Prevents "intercept 400→200" from
      // showing next screen with a minimal fake body; token is only issued on real server-side success.
      const token = verifyResponse?.verificationToken;
      if (!token || typeof token !== "string" || !token.trim()) {
        throw new Error("Invalid OTP");
      }
      setOtpVerificationToken(token);

      // OTP verified - proceed to second form
      setPolicyInfo(table);
      setAutoFilledEmail(!!email);
      setAutoFilledMobile(!!mobile);
      setSecondFormData((prev) => ({
        ...prev,
        email: email || prev.email,
        mobile: mobile || prev.mobile,
      }));
      setSecondFormIdentify(INITIAL_SECOND_IDENTIFY);
      setDobDateSecond(null);
      setShowOtpModal(false);
      setOtp(Array(OTP_LENGTH).fill(""));
      setOtpId(null);
      setPendingPolicyInfo(null);
      setOtpVerified(true);
      setIsFirstForm(false);
    } catch (err) {
      logError("[CreateGrievanceRedressal] verifyOTP failed", err);
      setOtpError("Invalid OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSecondChange = (e) => {
    const { name, value, files } = e.target;

    // EMAIL
    if (name === "email") {
      const emailValue = value.replace(/\s/g, ""); // block spaces

      setSecondFormData((prev) => ({
        ...prev,
        email: emailValue,
      }));

      const emailRegex =
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

      setErrors((prev) => ({
        ...prev,
        email: emailValue
          ? emailRegex.test(emailValue)
            ? ""
            : "Enter a valid email address"
          : "Email address is required",
      }));
      return;
    }

    // DOCUMENT
    if (name === "documents") {
      const file = files?.[0] || null;

      setSecondFormData((prev) => ({
        ...prev,
        documents: file,
      }));

      if (errors.documents) {
        setErrors((prev) => ({ ...prev, documents: "" }));
      }
      return;
    }

    // MOBILE
    if (name === "mobile") {
      const digits = value.replace(/\D/g, "").slice(0, 10);

      setSecondFormData((prev) => ({
        ...prev,
        mobile: digits,
      }));

      if (errors.mobile) {
        setErrors((prev) => ({ ...prev, mobile: "" }));
      }
      return;
    }

    // OTHER FIELDS
    setSecondFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };


  const handleSecondCustomerChange = (e) => {
    const isYes = e.target.value === "true";
    setSecondFormIsIndusindCustomer(isYes);
    setOtpVerified(false);
    setOtpVerificationToken(null); // Reset OTP verified flag when user changes selection
    if (isYes) {
      // If user selects "Yes" in second form, go back to first form
      // Reset second form data but keep the customer status
      setIsFirstForm(true);
      setFirstFormData((prev) => ({ ...prev, isIndusindCustomer: true }));
      setSecondFormData(INITIAL_SECOND);
      setSecondFormIdentify(INITIAL_SECOND_IDENTIFY);
      setDobDateSecond(null);
      setPolicyInfo(null);
      setAutoFilledEmail(false);
      setAutoFilledMobile(false);
    } else {
      // Show second form, hide first form
      setIsFirstForm(false);
      setSecondFormData((prev) => ({ ...prev, email: "", mobile: "" }));
      setPolicyInfo(null);
      setAutoFilledEmail(false);
      setAutoFilledMobile(false);
      setSecondFormIdentify(INITIAL_SECOND_IDENTIFY);
      setDobDateSecond(null);
    }
    setErrors((prev) => ({ ...prev, email: "", mobile: "" }));
  };

  const handleSecondIdentifyByChange = (e) => {
    const value = e.target.value;
    setSecondFormIdentify((prev) => ({
      ...prev,
      identifyBy: value,
      policyNumber: "",
      clientId: "",
      mobileNumber: "",
      dob: "",
    }));
    setDobDateSecond(null);
    setErrors((prev) => ({
      ...prev,
      policyNumber: "",
      clientId: "",
      mobileNumber: "",
      dob: "",
    }));
  };

  const handleSecondIdentifyChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === "mobileNumber") v = value.replace(/\D/g, "").slice(0, 10);
    setSecondFormIdentify((prev) => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSecondDobChange = (date) => {
    setDobDateSecond(date);
    // Format as DD-MM-YYYY for backend
    const formatted = date
      ? `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`
      : "";
    setSecondFormIdentify((prev) => ({ ...prev, dob: formatted }));
    if (errors.dob) setErrors((prev) => ({ ...prev, dob: "" }));
  };

  const validateSecondIdentify = () => {
    const newErrors = {};
    const { identifyBy, policyNumber, clientId, mobileNumber, dob } =
      secondFormIdentify;
    if (identifyBy === IDENTIFY_BY.POLICY_NUMBER) {
      const v = (policyNumber || "").replace(/\s/g, "");
      if (!v) newErrors.policyNumber = "Policy number is required";
    } else if (identifyBy === IDENTIFY_BY.CLIENT_ID) {
      if (!(clientId || "").trim())
        newErrors.clientId = "Client ID is required";
    } else if (identifyBy === IDENTIFY_BY.MOBILE_AND_DOB) {
      const mob = (mobileNumber || "").replace(/\D/g, "");
      if (mob.length !== 10)
        newErrors.mobileNumber = "Enter valid 10-digit mobile number";
      if (!(dob || "").trim()) newErrors.dob = "Date of birth is required";
    }
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const buildSecondApiParams = () => {
    const { identifyBy, policyNumber, clientId, mobileNumber, dob } =
      secondFormIdentify;
    if (identifyBy === IDENTIFY_BY.POLICY_NUMBER) {
      return { policyNumber: (policyNumber || "").replace(/\s/g, "") };
    }
    if (identifyBy === IDENTIFY_BY.CLIENT_ID) {
      return { clientId: (clientId || "").trim() };
    }
    return {
      mobileNumber: (mobileNumber || "").replace(/\D/g, ""),
      dob: (dob || "").trim(),
    };
  };

  const handleFetchDetails = async (e) => {
    e.preventDefault();
    setApiErrorMsg("");
    if (!validateSecondIdentify()) return;

    setLoading(true);
    try {
      const params = buildSecondApiParams();
      const response = await invoke.getLatestPolicyInfo(params);

      if (!response) {
        setApiErrorMsg(
          "Unable to fetch policy details. Please try again later.",
        );
        setShowErrorModal(true);
        return;
      }

      if (typeof response?.operationResponse === "string") {
        setApiErrorMsg(
          response.operationResponse ||
          "No records found for the provided details.",
        );
        setShowErrorModal(true);
        return;
      }

      const table = response?.operationResponse?.Table;
      if (!table) {
        setApiErrorMsg("No records found for the provided details.");
        setShowErrorModal(true);
        return;
      }

      const email = (table.Email || "").trim();
      const mobile = (table.Mobile || table.Telephone1 || "")
        .trim()
        .replace(/\D/g, "")
        .slice(-10);

      setPolicyInfo(table);
      setAutoFilledEmail(!!email);
      setAutoFilledMobile(!!mobile);
      setSecondFormData((prev) => ({
        ...prev,
        email: email || prev.email,
        mobile: mobile || prev.mobile,
      }));
    } catch (err) {
      logError(
        "[CreateGrievanceRedressal] getLatestPolicyInfo (fetch) failed",
        err,
      );
      setApiErrorMsg(
        err?.response?.data?.error || "Something went wrong. Please try again.",
      );
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const validateSecondForm = () => {
    const newErrors = {};
    const { email, mobile, grievanceType, grievanceDescription } =
      secondFormData;

    const emailErr = validateCareerEmail(email);
    if (emailErr) newErrors.email = emailErr;

    const mobileErr = validateMobileNumber(mobile);
    if (mobileErr) newErrors.mobile = mobileErr;

    const typeErr = validateRequired(grievanceType, "Grievance type");
    if (typeErr) newErrors.grievanceType = typeErr;

    const descErr = validateRequired(grievanceDescription, "Grievance details");
    if (descErr) newErrors.grievanceDescription = descErr;
    else if ((grievanceDescription || "").length > GRIEVANCE_DESC_MAX) {
      newErrors.grievanceDescription = `Maximum ${GRIEVANCE_DESC_MAX} characters allowed`;
    }

    const file = secondFormData.documents;
    if (file) {
      const fileErr = validateFileSize(file, FILE_MAX_MB);
      if (fileErr) newErrors.documents = fileErr;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildBasePayload = () => {
    const isExistingCustomer = secondFormIsIndusindCustomer ? "Yes" : "No";
    return {
      isExistingCustomer: isExistingCustomer,
      policyNo: "",
      clientNo: "",
      emailId: "",
      mobileNo: "",
      description: secondFormData.grievanceType || "",
      escalationLevel: "",
      grievanceNo: "",
      grievanceComment: secondFormData.grievanceDescription || "",
      attachments: [],
      fileNames: [],
    };
  };

  const populateExistingCustomerPayload = (payload) => {
    const firstFormPolicyNo =
      firstFormData.identifyBy === IDENTIFY_BY.POLICY_NUMBER
        ? (firstFormData.policyNumber || "").replace(/\s/g, "")
        : "";
    const firstFormClientNo =
      firstFormData.identifyBy === IDENTIFY_BY.CLIENT_ID
        ? (firstFormData.clientId || "").trim()
        : "";
    const secondFormPolicyNo =
      secondFormIdentify.identifyBy === IDENTIFY_BY.POLICY_NUMBER
        ? (secondFormIdentify.policyNumber || "").replace(/\s/g, "")
        : "";
    const secondFormClientNo =
      secondFormIdentify.identifyBy === IDENTIFY_BY.CLIENT_ID
        ? (secondFormIdentify.clientId || "").trim()
        : "";

    payload.policyNo = (
      policyInfo?.PolicyNumber ||
      policyInfo?.PolicyNo ||
      secondFormPolicyNo ||
      firstFormPolicyNo ||
      ""
    ).trim();
    payload.clientNo = (
      policyInfo?.ClientId ||
      policyInfo?.ClientNo ||
      secondFormClientNo ||
      firstFormClientNo ||
      ""
    ).trim();
    const policyEmail = (policyInfo?.Email || "").trim();
    const policyMobile = (policyInfo?.Mobile || policyInfo?.Telephone1 || "")
      .trim()
      .replace(/\D/g, "")
      .slice(-10);
    payload.emailId = policyEmail || secondFormData.email || "";
    payload.mobileNo = policyMobile || secondFormData.mobile || "";
  };

  const populateNonExistingCustomerPayload = (payload) => {
    payload.policyNo = "policy";
    payload.clientNo = "1";
    payload.emailId = secondFormData.email || "";
    payload.mobileNo = secondFormData.mobile || "";
  };

  const handleSecondContinue = async (e) => {
    e.preventDefault();
    if (!validateSecondForm()) return;

    setLoading(true);
    setApiErrorMsg("");

    try {
      const payload = buildBasePayload();
      const isExistingCustomer = secondFormIsIndusindCustomer;

      if (isExistingCustomer) {
        populateExistingCustomerPayload(payload);
        if (otpVerificationToken) {
          payload.otpVerificationToken = otpVerificationToken;
        }
      } else {
        populateNonExistingCustomerPayload(payload);
      }

      if (secondFormData.documents) {
        try {
          const base64 = await fileToBase64(secondFormData.documents);
          payload.attachments = [base64];
          payload.fileNames = [secondFormData.documents.name];
        } catch (err) {
          logError("[CreateGrievanceRedressal] file to base64 failed", err);
          setApiErrorMsg("Failed to read file. Please try again.");
          setShowErrorModal(true);
          setLoading(false);
          return;
        }
      }

      const response = await invoke.createGrievancesSR(payload);

      if (!response) {
        setApiErrorMsg("Unable to submit grievance. Please try again later.");
        setShowErrorModal(true);
        setLoading(false);
        return;
      }

      if (response?.infoCode === INFO_CODE_SERVICE_REQUEST_ALREADY_OPEN) {
        setAlreadyOpenMsg(
          response?.message || SERVICE_REQUEST_ALREADY_OPEN_MESSAGE,
        );
        setShowAlreadyOpenModal(true);
        return;
      }

      const grievanceNo =
        response?.operationResponse != null
          ? (typeof response.operationResponse === "string"
            ? response.operationResponse
            : String(response.operationResponse))
          : response?.CreateGrievancesSRResult != null
            ? (typeof response.CreateGrievancesSRResult === "string"
              ? response.CreateGrievancesSRResult
              : String(response.CreateGrievancesSRResult))
            : "";
      if (grievanceNo || response?.operationResponse) {
        setSubmittedGrievanceNo(grievanceNo);
        setShowSuccessModal(true);
      } else {
        const errorMsg =
          response?.error ||
          response?.message ||
          "Submission failed. Please try again.";
        setApiErrorMsg(errorMsg);
        setShowErrorModal(true);
      }
    } catch (err) {
      logError("[CreateGrievanceRedressal] submit failed", err);
      const errorMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Submission failed. Please try again.";
      setApiErrorMsg(errorMsg);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const successMessage = submittedGrievanceNo
    ? `Your grievance no. ${submittedGrievanceNo} has been submitted successfully. We will get back to you shortly.`
    : "Your grievance has been submitted successfully. We will get back to you shortly.";

  return (
    <div className="create-grievance-wrapper">
      {(loading || otpLoading) && (
        <div className="loader-overlay">
          <BrandLoader
            color="#97262A"
            loading={loading || otpLoading}
            size={15}
          />
        </div>
      )}
      <div className="create-grievance">
        <div className="create-grievance__card">
          <div className="create-grievance__header">
            <h4 className="create-grievance__title">
              {pageTitle || DEFAULT_HEADER_TITLE}
            </h4>
          </div>

          {isFirstForm ? (
            <form
              className="create-grievance__form"
              onSubmit={handleFirstContinue}
            >
              <div className="form-row form-row--radio-group">
                <span className="label-one">
                  Are you a Indusind Nippon Life Insurance Customer?
                </span>
                <div className="radio-group-inline">
                  <label
                    className={`radio-pill ${firstFormData.isIndusindCustomer === true ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="isIndusindCustomer"
                      value="true"
                      checked={firstFormData.isIndusindCustomer === true}
                      onChange={handleFirstChange}
                    />
                    Yes
                  </label>
                  <label
                    className={`radio-pill ${firstFormData.isIndusindCustomer === false ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="isIndusindCustomer"
                      value="false"
                      checked={firstFormData.isIndusindCustomer === false}
                      onChange={handleFirstChange}
                    />
                    No
                  </label>
                </div>
              </div>

              {firstFormData.isIndusindCustomer && (
                <>
                  <div className="form-row form-row--identify">
                    <div className="radio-list">
                      <div
                        role="button"
                        tabIndex={0}
                        className={`radio-option ${firstFormData.identifyBy === IDENTIFY_BY.POLICY_NUMBER ? "selected" : ""}`}
                        onClick={handleRadioOptionBlockClick(
                          IDENTIFY_BY.POLICY_NUMBER,
                          handleIdentifyByChange,
                        )}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleIdentifyByChange({
                              target: { value: IDENTIFY_BY.POLICY_NUMBER },
                            });
                          }
                        }}
                      >
                        <label className="radio-option-label">
                          <input
                            type="radio"
                            name="identifyBy"
                            value={IDENTIFY_BY.POLICY_NUMBER}
                            checked={
                              firstFormData.identifyBy ===
                              IDENTIFY_BY.POLICY_NUMBER
                            }
                            onChange={handleIdentifyByChange}
                          />
                          <span>Policy Number</span>
                        </label>
                        {firstFormData.identifyBy ===
                          IDENTIFY_BY.POLICY_NUMBER && (
                            <input
                              type="text"
                              name="policyNumber"
                              value={firstFormData.policyNumber}
                              onChange={handleFirstChange}
                              placeholder="e.g. 1234 5678 9012 34"
                              className={`form-input inline-input ${errors.policyNumber ? "has-error" : ""}`}
                              autoComplete="off"
                            />
                          )}
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        className={`radio-option ${firstFormData.identifyBy === IDENTIFY_BY.CLIENT_ID ? "selected" : ""}`}
                        onClick={handleRadioOptionBlockClick(
                          IDENTIFY_BY.CLIENT_ID,
                          handleIdentifyByChange,
                        )}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleIdentifyByChange({
                              target: { value: IDENTIFY_BY.CLIENT_ID },
                            });
                          }
                        }}
                      >
                        <label className="radio-option-label">
                          <input
                            type="radio"
                            name="identifyBy"
                            value={IDENTIFY_BY.CLIENT_ID}
                            checked={
                              firstFormData.identifyBy === IDENTIFY_BY.CLIENT_ID
                            }
                            onChange={handleIdentifyByChange}
                          />
                          <span>Client ID</span>
                        </label>
                        {firstFormData.identifyBy === IDENTIFY_BY.CLIENT_ID && (
                          <input
                            type="text"
                            name="clientId"
                            value={firstFormData.clientId}
                            onChange={handleFirstChange}
                            placeholder="e.g. 90866750"
                            className={`form-input inline-input ${errors.clientId ? "has-error" : ""}`}
                            autoComplete="off"
                          />
                        )}
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        className={`radio-option ${firstFormData.identifyBy === IDENTIFY_BY.MOBILE_AND_DOB ? "selected" : ""}`}
                        onClick={handleRadioOptionBlockClick(
                          IDENTIFY_BY.MOBILE_AND_DOB,
                          handleIdentifyByChange,
                        )}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleIdentifyByChange({
                              target: { value: IDENTIFY_BY.MOBILE_AND_DOB },
                            });
                          }
                        }}
                      >
                        <label className="radio-option-label">
                          <input
                            type="radio"
                            name="identifyBy"
                            value={IDENTIFY_BY.MOBILE_AND_DOB}
                            checked={
                              firstFormData.identifyBy ===
                              IDENTIFY_BY.MOBILE_AND_DOB
                            }
                            onChange={handleIdentifyByChange}
                          />
                          <span>Mobile Number &amp; DOB</span>
                        </label>
                        {firstFormData.identifyBy ===
                          IDENTIFY_BY.MOBILE_AND_DOB && (
                            <div className="inline-fields">
                              <input
                                type="text"
                                name="mobileNumber"
                                value={firstFormData.mobileNumber}
                                onChange={handleFirstChange}
                                placeholder="e.g. 9924402676"
                                className={`form-input inline-input ${errors.mobileNumber ? "has-error" : ""}`}
                                autoComplete="off"
                                inputMode="numeric"
                                maxLength={10}
                              />
                              <DatePicker
                                selected={dobDate}
                                onChange={handleDobChange}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="DOB (DD-MM-YYYY)"
                                maxDate={new Date()}
                                className={`form-input inline-input dob-input ${errors.dob ? "has-error" : ""}`}
                                autoComplete="off"
                              />
                              {(errors.mobileNumber || errors.dob) && (
                                <span className="error">
                                  {errors.mobileNumber || errors.dob}
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                    {errors.policyNumber && (
                      <span className="error">{errors.policyNumber}</span>
                    )}
                    {errors.clientId && (
                      <span className="error">{errors.clientId}</span>
                    )}
                  </div>
                </>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="grievance-button btn primary-button regular"
                  disabled={loading || !isCustomerSelected || !radioField}
                >
                  {loading ? "Loading..." : "Continue"}
                </button>
              </div>
            </form>
          ) : (
            <form
              className="create-grievance__form"
              onSubmit={handleSecondContinue}
            >
              <div className="form-row form-row--radio-group">
                <span className="label-one">
                  Are you a Indusind Nippon Life Insurance Customer?
                </span>
                <div className="radio-group-inline">
                  <label
                    className={`radio-pill ${secondFormIsIndusindCustomer ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="secondFormIsIndusindCustomer"
                      value="true"
                      checked={secondFormIsIndusindCustomer}
                      onChange={handleSecondCustomerChange}
                    />
                    Yes
                  </label>
                  <label
                    className={`radio-pill ${!secondFormIsIndusindCustomer ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="secondFormIsIndusindCustomer"
                      value="false"
                      checked={!secondFormIsIndusindCustomer}
                      onChange={handleSecondCustomerChange}
                    />
                    No
                  </label>
                </div>
              </div>

              {secondFormIsIndusindCustomer && !otpVerified && (
                <div className="form-row form-row--identify form-row--identify-second">
                  <span className="label-one">Identify by</span>
                  <div className="radio-list">
                    <div
                      role="button"
                      tabIndex={0}
                      className={`radio-option ${secondFormIdentify.identifyBy === IDENTIFY_BY.POLICY_NUMBER ? "selected" : ""}`}
                      onClick={handleRadioOptionBlockClick(
                        IDENTIFY_BY.POLICY_NUMBER,
                        handleSecondIdentifyByChange,
                      )}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSecondIdentifyByChange({
                            target: { value: IDENTIFY_BY.POLICY_NUMBER },
                          });
                        }
                      }}
                    >
                      <label className="radio-option-label">
                        <input
                          type="radio"
                          name="secondIdentifyBy"
                          value={IDENTIFY_BY.POLICY_NUMBER}
                          checked={
                            secondFormIdentify.identifyBy ===
                            IDENTIFY_BY.POLICY_NUMBER
                          }
                          onChange={handleSecondIdentifyByChange}
                        />
                        <span>Policy Number</span>
                      </label>
                      {secondFormIdentify.identifyBy ===
                        IDENTIFY_BY.POLICY_NUMBER && (
                          <input
                            type="text"
                            name="policyNumber"
                            value={secondFormIdentify.policyNumber}
                            onChange={handleSecondIdentifyChange}
                            placeholder="e.g. 1234 5678 9012 34"
                            className={`form-input inline-input ${errors.policyNumber ? "has-error" : ""}`}
                            autoComplete="off"
                          />
                        )}
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      className={`radio-option ${secondFormIdentify.identifyBy === IDENTIFY_BY.CLIENT_ID ? "selected" : ""}`}
                      onClick={handleRadioOptionBlockClick(
                        IDENTIFY_BY.CLIENT_ID,
                        handleSecondIdentifyByChange,
                      )}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSecondIdentifyByChange({
                            target: { value: IDENTIFY_BY.CLIENT_ID },
                          });
                        }
                      }}
                    >
                      <label className="radio-option-label">
                        <input
                          type="radio"
                          name="secondIdentifyBy"
                          value={IDENTIFY_BY.CLIENT_ID}
                          checked={
                            secondFormIdentify.identifyBy ===
                            IDENTIFY_BY.CLIENT_ID
                          }
                          onChange={handleSecondIdentifyByChange}
                        />
                        <span>Client ID</span>
                      </label>
                      {secondFormIdentify.identifyBy ===
                        IDENTIFY_BY.CLIENT_ID && (
                          <input
                            type="text"
                            name="clientId"
                            value={secondFormIdentify.clientId}
                            onChange={handleSecondIdentifyChange}
                            placeholder="e.g. 90866750"
                            className={`form-input inline-input ${errors.clientId ? "has-error" : ""}`}
                            autoComplete="off"
                          />
                        )}
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      className={`radio-option ${secondFormIdentify.identifyBy === IDENTIFY_BY.MOBILE_AND_DOB ? "selected" : ""}`}
                      onClick={handleRadioOptionBlockClick(
                        IDENTIFY_BY.MOBILE_AND_DOB,
                        handleSecondIdentifyByChange,
                      )}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSecondIdentifyByChange({
                            target: { value: IDENTIFY_BY.MOBILE_AND_DOB },
                          });
                        }
                      }}
                    >
                      <label className="radio-option-label">
                        <input
                          type="radio"
                          name="secondIdentifyBy"
                          value={IDENTIFY_BY.MOBILE_AND_DOB}
                          checked={
                            secondFormIdentify.identifyBy ===
                            IDENTIFY_BY.MOBILE_AND_DOB
                          }
                          onChange={handleSecondIdentifyByChange}
                        />
                        <span>Mobile Number &amp; DOB</span>
                      </label>
                      {secondFormIdentify.identifyBy ===
                        IDENTIFY_BY.MOBILE_AND_DOB && (
                          <div className="inline-fields">
                            <input
                              type="text"
                              name="mobileNumber"
                              value={secondFormIdentify.mobileNumber}
                              onChange={handleSecondIdentifyChange}
                              placeholder="e.g. 9924402676"
                              className={`form-input inline-input ${errors.mobileNumber ? "has-error" : ""}`}
                              autoComplete="off"
                              inputMode="numeric"
                              maxLength={10}
                            />
                            <DatePicker
                              selected={dobDateSecond}
                              onChange={handleSecondDobChange}
                              dateFormat="dd-MM-yyyy"
                              placeholderText="DOB (DD-MM-YYYY)"
                              maxDate={new Date()}
                              className={`form-input inline-input dob-input ${errors.dob ? "has-error" : ""}`}
                              autoComplete="off"
                            />
                            {(errors.mobileNumber || errors.dob) && (
                              <span className="error">
                                {errors.mobileNumber || errors.dob}
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                  {errors.policyNumber && (
                    <span className="error">{errors.policyNumber}</span>
                  )}
                  {errors.clientId && (
                    <span className="error">{errors.clientId}</span>
                  )}
                  <div className="form-actions form-actions--fetch">
                    <button
                      type="button"
                      className="grievance-button btn fetch-button"
                      onClick={handleFetchDetails}
                      disabled={loading}
                    >
                      {loading ? "Fetching..." : "Fetch details"}
                    </button>
                  </div>
                </div>
              )}

              <div className="two-row-box">
                <div className="form-row">
                  <label className="label-one" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={secondFormData.email}
                    onChange={handleSecondChange}
                    placeholder="e.g. deepaksharma@gmail.com"
                    className={`form-input ${errors.email ? "has-error" : ""} ${autoFilledEmail ? "read-only" : ""}`}
                    autoComplete="email"
                    readOnly={autoFilledEmail}
                  />
                  {errors.email && <span className="error">{errors.email}</span>}
                </div>

                <div className="form-row">
                  <label className="label-one" htmlFor="mobile">
                    Mobile Number
                  </label>
                  <div className="flag-input">
                    <div className="flag-box">
                      <img
                        className="country-flag"
                        src={IndiaFlag}
                        alt="India"
                      />
                      <span className="dial-code">+91</span>
                    </div>
                    <input
                      id="mobile"
                      name="mobile"
                      type="text"
                      value={secondFormData.mobile}
                      onChange={handleSecondChange}
                      placeholder="e.g. 98762 72383"
                      className={`form-input ${errors.mobile ? "has-error" : ""} ${autoFilledMobile ? "read-only" : ""}`}
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={10}
                      readOnly={autoFilledMobile}
                    />
                  </div>
                  {errors.mobile && (
                    <span className="error">{errors.mobile}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <label className="label-one" htmlFor="grievanceType">
                  Your Grievance is related to
                </label>
                <select
                  id="grievanceType"
                  name="grievanceType"
                  value={secondFormData.grievanceType}
                  onChange={handleSecondChange}
                  className={`form-input ${errors.grievanceType ? "has-error" : ""}`}
                >
                  <option value="">Select</option>
                  {GRIEVANCE_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {errors.grievanceType && (
                  <span className="error">{errors.grievanceType}</span>
                )}
              </div>

              <div className="form-row">
                <label className="label-one" htmlFor="grievanceDescription">
                  Enter the details of your grievance{" "}
                  <span className="tag-span">
                    ({GRIEVANCE_DESC_MAX} characters)
                  </span>
                </label>
                <textarea
                  id="grievanceDescription"
                  name="grievanceDescription"
                  value={secondFormData.grievanceDescription}
                  onChange={handleSecondChange}
                  placeholder="write here in brief."
                  className={`form-input form-textarea ${errors.grievanceDescription ? "has-error" : ""}`}
                  rows={5}
                  maxLength={GRIEVANCE_DESC_MAX}
                />
                <div className="char-count">
                  {secondFormData.grievanceDescription.length} /{" "}
                  {GRIEVANCE_DESC_MAX}
                </div>
                {errors.grievanceDescription && (
                  <span className="error">{errors.grievanceDescription}</span>
                )}
              </div>

              <div className="form-row">
                <label className="label-one" htmlFor="documents">
                  Upload Relevant Documents{" "}
                  <span className="tag-span">
                    (max. {FILE_MAX_MB} mb jpg, png, pdf)
                  </span>
                </label>
                <label className="file-upload-zone" htmlFor="documents">
                  <input
                    id="documents"
                    name="documents"
                    type="file"
                    accept={FILE_ACCEPT}
                    onChange={handleSecondChange}
                    className="file-input-hidden"
                  />
                  <div className="file-upload-placeholder">
                    <span className="file-upload-icon">
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15 9.37549V14.3755C15 14.5413 14.9342 14.7002 14.8169 14.8174C14.6997 14.9346 14.5408 15.0005 14.375 15.0005H0.625C0.45924 15.0005 0.300269 14.9346 0.183058 14.8174C0.0658481 14.7002 0 14.5413 0 14.3755V9.37549C0 9.20973 0.0658481 9.05076 0.183058 8.93355C0.300269 8.81634 0.45924 8.75049 0.625 8.75049C0.79076 8.75049 0.949731 8.81634 1.06694 8.93355C1.18415 9.05076 1.25 9.20973 1.25 9.37549V13.7505H13.75V9.37549C13.75 9.20973 13.8158 9.05076 13.9331 8.93355C14.0503 8.81634 14.2092 8.75049 14.375 8.75049C14.5408 8.75049 14.6997 8.81634 14.8169 8.93355C14.9342 9.05076 15 9.20973 15 9.37549ZM4.81719 4.19268L6.875 2.13409V9.37549C6.875 9.54125 6.94085 9.70022 7.05806 9.81743C7.17527 9.93464 7.33424 10.0005 7.5 10.0005C7.66576 10.0005 7.82473 9.93464 7.94194 9.81743C8.05915 9.70022 8.125 9.54125 8.125 9.37549V2.13409L10.1828 4.19268C10.3001 4.30995 10.4591 4.37584 10.625 4.37584C10.7909 4.37584 10.9499 4.30995 11.0672 4.19268C11.1845 4.0754 11.2503 3.91634 11.2503 3.75049C11.2503 3.58464 11.1845 3.42558 11.0672 3.3083L7.94219 0.183304C7.88414 0.125194 7.81521 0.0790944 7.73934 0.0476417C7.66346 0.016189 7.58213 0 7.5 0C7.41787 0 7.33654 0.016189 7.26066 0.0476417C7.18479 0.0790944 7.11586 0.125194 7.05781 0.183304L3.93281 3.3083C3.81554 3.42558 3.74965 3.58464 3.74965 3.75049C3.74965 3.91634 3.81554 4.0754 3.93281 4.19268C4.05009 4.30995 4.20915 4.37584 4.375 4.37584C4.54085 4.37584 4.69991 4.30995 4.81719 4.19268Z"
                          fill="#97262A"
                        />
                      </svg>
                    </span>
                    <span className="file-upload-text">
                      {secondFormData.documents
                        ? secondFormData.documents.name
                        : ""}
                    </span>
                  </div>
                </label>
                {errors.documents && (
                  <span className="error">{errors.documents}</span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="grievance-button btn primary-button"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Continue"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* SUCCESS MODAL - same design as other forms; form stays visible behind */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        type="success"
        btnTitle="Close"
      >
        <p>{successMessage}</p>
      </Modal>

      <Modal
        isOpen={showAlreadyOpenModal}
        onClose={() => {
          setShowAlreadyOpenModal(false);
          setAlreadyOpenMsg("");
        }}
        title="Request already registered"
        type="success"
        btnTitle="Close"
      >
        <p>{alreadyOpenMsg}</p>
      </Modal>

      <Modal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setApiErrorMsg("");
        }}
        title="Something went wrong!"
        type="error"
        btnTitle="Try Again"
      >
        <p>{apiErrorMsg}</p>
      </Modal>

      {showOtpModal && (
        <div className="otp-modal-backdrop">
          <div className="otp-modal">
            <h6 className="large-body">Verify OTP</h6>
            <p className="small-body">
              Enter the 6-digit OTP sent to your mobile number
            </p>

            {otpLoading && (
              <div className="otp-loader">
                <BrandLoader color="#97262A" loading={otpLoading} size={10} />
              </div>
            )}

            <div className="otp-inputs">
              {otpSlots.current.map(({ id, pos }) => (
                <input
                  key={id}
                  ref={(el) => (otpRefs.current[pos] = el)}
                  data-pos={pos}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={otp[pos]}
                  disabled={otpLoading}
                  onChange={handleOtpChange}
                  onKeyDown={handleOtpKeyDown}
                  onPaste={handleOtpPaste}
                />
              ))}
            </div>

            {otpError && <div className="error-text">{otpError}</div>}

            <div className="otp-actions">
              <button
                className="modal-close"
                onClick={() => {
                  setShowOtpModal(false);
                  setOtp(Array(OTP_LENGTH).fill(""));
                  setOtpError(null);
                  setPendingPolicyInfo(null);
                  setOtpVerified(false);
                  setOtpVerificationToken(null);
                }}
                disabled={otpLoading}
              >
                ✕
              </button>
              <button
                className="modal-btn btn primary-button regular"
                onClick={handleVerifyOtp}
                disabled={otpLoading}
              >
                {otpLoading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateGrievanceRedressalForm;
